// FLAGS: health-data, irreversible, silent-failure
// Availability reads plus the four booking writes staff make on a client's behalf: book, book-with-expert, cancel,
// reschedule. Every one of them moves a real appointment and notifies the client.
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queryIdentifiers } from "@src/utils/constants";
import moment, { Moment } from "moment";
import { getWeekStartQueryKey } from "@src/utils/formatter";
import { Scheduler } from "@src/api/v1/Scheduler";
import { RequestTag } from "@src/api/v1/data-contracts";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { consultStaleTime } from "./staletimes";

export const identifier = "scheduler";

export type TimeSpan = {
  startTime: string;
  endTime?: string;
};
export type GroupedTimeSlot = {
  date: string;
  timeSlots: TimeSpan[];
};

const groupByTimeSlotsByDate = (
  timeSlots: TimeSpan[],
  timeZone: string,
): GroupedTimeSlot[] => {
  const startDateInTimeZone = moment.tz(timeSlots[0].startTime, timeZone);

  const startDateOfWeek = moment.utc(startDateInTimeZone).startOf("isoWeek");
  const endDateOfWeek = moment.utc(startDateInTimeZone).endOf("isoWeek");
  let date = startDateOfWeek;

  const result = new Array(7);
  let counter = 0;
  while (endDateOfWeek.isSameOrAfter(date)) {
    const timeSlotsInDay = timeSlots.filter(
      (timeSlot) =>
        moment.tz(timeSlot.startTime, timeZone).format("LL") ===
        date.format("LL"),
    );
    result[counter] = { date: date.toISOString(), timeSlots: timeSlotsInDay };
    counter++;
    date = moment(date).add(1, "days");
  }
  return result;
};
const createWeekWithNoAvailableTimeSlots = (
  startDateOfWeek: Moment,
): GroupedTimeSlot[] => {
  const endDateOfWeek = moment(startDateOfWeek).utc().endOf("isoWeek");
  const result = [];
  let date = startDateOfWeek;
  while (date <= endDateOfWeek) {
    result.push({ date: date.toISOString(), timeSlots: [] });
    date = moment(date).add(1, "day");
  }
  return result;
};
// FLAGS: silent-failure
// Writes keys of the form `[availableTimeSlots, timeZone, weekStart]` — no expert segment — while the per-expert hook
// reads `[…, weekStart, psychologistId]`. So it fills the *current-expert* namespace on behalf of whichever expert was
// queried, and the empty-week placeholders it writes look identical to a real "no availability" answer.
const updateAvailableSlotsCache = (
  startDate: Moment,
  timeZone: string,
  data: GroupedTimeSlot[],
  queryClient: QueryClient,
) => {
  if (!Array.isArray(data) || data.length === 0) return;

  const startDateOfWeekWithAvailableSlots = moment(data[0].date);
  if (startDateOfWeekWithAvailableSlots.isAfter(startDate)) {
    let startDateOfWeek = moment(startDate).startOf("isoWeek");

    while (startDateOfWeekWithAvailableSlots.isAfter(startDateOfWeek)) {
      queryClient.setQueryData(
        [
          queryIdentifiers.availableTimeSlots,
          timeZone,
          getWeekStartQueryKey(startDateOfWeek),
        ],
        createWeekWithNoAvailableTimeSlots(startDateOfWeek),
      );

      startDateOfWeek = startDateOfWeek.add(1, "week");
    }

    queryClient.setQueryData(
      [
        queryIdentifiers.availableTimeSlots,
        timeZone,
        getWeekStartQueryKey(startDateOfWeekWithAvailableSlots),
      ],
      data,
    );
  }
};

export const useSchedulerGetAvailableSlotsForPsychologist = (
  timeZone: string,
  startDate: string,
  endDate: string,
  psychologistId: string | undefined,
) => {
  const startDateOfWeek = moment(startDate).utc().startOf("isoWeek");
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [
      queryIdentifiers.availableTimeSlots,
      timeZone,
      getWeekStartQueryKey(startDateOfWeek),
      psychologistId,
    ],
    queryFn: async () => {
      const data = await new Scheduler(
        OpenUpHttpClient,
      ).getTimeSlotsInFirstAvailableWeek({
        psychologistId,
        timeZone,
        startDate: startDateOfWeek.toISOString(),
        endDate,
      });
      let returnValue;
      if (!endDate && data.length === 0) {
        // handle no available slots
        returnValue = null;
      } else if (data.length === 0) {
        returnValue = createWeekWithNoAvailableTimeSlots(startDateOfWeek);
      } else returnValue = groupByTimeSlotsByDate(data, timeZone);
      if (returnValue) {
        updateAvailableSlotsCache(
          startDateOfWeek,
          timeZone,
          returnValue,
          queryClient,
        );
      } else {
        queryClient.setQueryData(
          [
            queryIdentifiers.availableTimeSlots,
            timeZone,
            getWeekStartQueryKey(startDateOfWeek),
          ],
          returnValue,
        );
      }
      return returnValue;
    },
    staleTime: consultStaleTime,
    enabled: !!startDate && !!psychologistId,
  });
};

export const useGetAvailableSlotsForCurrentPsychologist = (
  timeZone: string,
  startDate: string,
  endDate: string,
) => {
  const startDateOfWeek = moment(startDate).utc().startOf("isoWeek");
  const queryClient = useQueryClient();
  const queryKey = [
    queryIdentifiers.availableTimeSlots,
    timeZone,
    getWeekStartQueryKey(startDateOfWeek),
  ];
  return useQuery({
    queryKey,
    queryFn: async () => {
      const data = await new Scheduler(
        OpenUpHttpClient,
      ).getTimeSlotsInFirstAvailableWeek2({
        timeZone,
        startDate: startDateOfWeek.toISOString(),
        endDate,
      });
      let returnValue;
      if (!endDate && data.length === 0) {
        returnValue = null;
      } else if (data.length === 0) {
        returnValue = createWeekWithNoAvailableTimeSlots(startDateOfWeek);
      } else {
        returnValue = groupByTimeSlotsByDate(data, timeZone);
      }

      if (returnValue) {
        updateAvailableSlotsCache(
          startDateOfWeek,
          timeZone,
          returnValue,
          queryClient,
        );
      }
      return returnValue;
    },
    staleTime: consultStaleTime,
    enabled: !!startDate,
  });
};

export const useSchedulerBookConsultWithClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      time,
      timezone,
      tags,
      clientId,
    }: {
      time: string;
      timezone: string;
      tags: RequestTag[];
      clientId: string;
    }) =>
      new Scheduler(OpenUpHttpClient).bookConsultWithClient(clientId, {
        time,
        timezone,
        tags,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.availableTimeSlots],
      });
      queryClient.invalidateQueries({
        queryKey: ["clients", data.clientId, "consults"],
      });
      queryClient.invalidateQueries({
        queryKey: ["client-consults", data.clientId],
      });
    },
  });
};

export const useSchedulerBookConsultWithClientAndPsychologist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      time,
      timezone,
      tags,
      clientId,
      psychologistId,
    }: {
      time: string;
      timezone: string;
      tags: RequestTag[];
      clientId: string;
      psychologistId: string;
    }) =>
      new Scheduler(OpenUpHttpClient).bookConsultWithClientAndExpert(
        clientId,
        psychologistId,
        {
          time,
          timezone,
          tags,
        },
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.availableTimeSlots],
      });
      queryClient.invalidateQueries({
        queryKey: ["clients", data.clientId, "consults"],
      });
      queryClient.invalidateQueries({
        queryKey: ["client-consults", data.clientId],
      });
    },
  });
};

// FLAGS: health-data, irreversible
// Cancels a real appointment with a free-text reason; the client is notified and the slot is released. Nothing here
// re-books, so a mistaken cancel is only recoverable by booking again into whatever is still free.
export const useCancelSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      consultId,
      reasonCancel,
    }: {
      consultId: number;
      reasonCancel: string;
    }) =>
      new Scheduler(OpenUpHttpClient).cancelConsult2(consultId, {
        reason: reasonCancel,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.availableTimeSlots],
      });
      queryClient.invalidateQueries({
        queryKey: ["clients", data.clientId, "consults"],
      });
      queryClient.setQueryData(
        [identifier, data.id, "consult", "cancel"],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.psychologists],
      });
    },
  });
};

// FLAGS: health-data, irreversible
// Moves an existing appointment. `endStart` is forwarded from the caller and the current picker never supplies it —
// inert only because the endpoint re-derives the end from the configured session length.
export const useRescheduleSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      consultId,
      startTime,
      endTime,
      reasonReschedule,
      timezone,
    }: {
      consultId: number;
      startTime: string;
      endTime: string;
      reasonReschedule: string;
      timezone: string;
    }) =>
      new Scheduler(OpenUpHttpClient).rescheduleConsult3(consultId, {
        newStart: startTime,
        endStart: endTime,
        reason: reasonReschedule,
        timezone,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.availableTimeSlots],
      });
      queryClient.invalidateQueries({
        queryKey: ["clients", data.clientId, "consults"],
      });
      queryClient.setQueryData(
        [identifier, data.id, "consult", "reschedule"],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [identifier, data.id, "consult", "reschedule"],
      });
      queryClient.invalidateQueries({
        queryKey: [queryIdentifiers.psychologists],
      });
    },
  });
};

export const useGetTimezones = () => {
  return useQuery({
    queryKey: [queryIdentifiers.timezones],
    queryFn: () => new Scheduler(OpenUpHttpClient).getTimezones(),
  });
};
